import { FC, useState } from 'react';
import styled from 'styled-components';
import delete_icon from '../../../../assets/delete_icon.svg';
import { Carousel } from '../../../../component/carousel';
import { DeleteModal } from '../../../../component/modal/deleteModal';
import {
  useChangeStatusMutation,
  useDeleteRoomTypeMutation
} from '../../../../store/hotel/api';
import { IRoomType } from '../../../../store/hotel/interface';
import { currencyFormatter } from '../../../../utils/currencyConverter';
import { pxToRem } from '../../../../utils/pxToRem';
import { showErrorToast, showSuccessToast } from '../../../../utils/toast';

type IRoomTypeCard = {
  room: IRoomType;
  hotelName: string;
};
export const RoomType: FC<IRoomTypeCard> = ({ room, hotelName }) => {
  const [deleteRoomTypeModal, setDeleteRoomTypeModal] = useState(false);
  const [changeStatus, { isLoading: isChangeStatusLoading }] =
    useChangeStatusMutation();
  const [availability, setAvailability] = useState(room.isAvailable);
  const [roomTypeId, setRoomTypeId] = useState<string>('');
  const [deleteRoomType, { isLoading: isDeleting }] =
    useDeleteRoomTypeMutation();
  const handleDeleteRoomType = (roomId: string) => {
    setDeleteRoomTypeModal(true);
    setRoomTypeId(roomId);
  };

  const handleAvailabilityStatus = (id: string) => {
    if (isChangeStatusLoading) return;
    setAvailability(!availability);
    changeStatus(id)
      .unwrap()
      .then((result) => {
        showSuccessToast(result.message);
        setAvailability(result.data.isAvailable);
      })
      .catch((error) => {
        showErrorToast(error.message);
        setAvailability(room.isAvailable);
      });
  };
  const handleRoomTypeDelete = () => {
    deleteRoomType(roomTypeId)
      .unwrap()
      .then((result) => {
        showSuccessToast(result.message);
        setDeleteRoomTypeModal(false);
      })
      .catch((error) => {
        showErrorToast(error.message);
      });
  };
  return (
    <Card>
      {deleteRoomTypeModal && (
        <DeleteModal
          deleteBtnClick={handleRoomTypeDelete}
          isLoading={isDeleting}
          cancelBtnClick={() => setDeleteRoomTypeModal(false)}
          deleteTitle="Delete room type"
          deleteText={`Are you sure you want to delete this room type from ${hotelName} Hotel, this action can’t be undone?`}
        />
      )}
      <CustomCarousel images={room.file} />
      <DeleteBtn>
        <Icon
          src={delete_icon}
          onClick={() => handleDeleteRoomType(room._id)}
        />
      </DeleteBtn>
      <RoomDetailContainer>
        <RoomDetail>{room.roomType}</RoomDetail>
        <RoomDetail>{`N${currencyFormatter(room.price)}`}</RoomDetail>
      </RoomDetailContainer>
      <NumberOfRooms>{`Count : ${room.noOfRooms} rooms`}</NumberOfRooms>
      <AvaliablityContainer>
        <Switch>
          <Input
            type="checkbox"
            checked={availability}
            onClick={() => handleAvailabilityStatus(room._id)}
          />
          <SliderRound id={'slider'} />
        </Switch>
        {isChangeStatusLoading
          ? 'loading...'
          : room.isAvailable
          ? 'Available'
          : 'Not available'}
      </AvaliablityContainer>
    </Card>
  );
};

const Card = styled.div`
  background: #ffffff;
  box-shadow: 0px 5.11803px 5.11803px rgba(50, 50, 71, 0.08),
    0px 5.11803px 10.2361px rgba(50, 50, 71, 0.06);
  border-radius: 8px;
  position: relative;
  padding: 8px 8px 16px 8px;
  width: calc(33.33% - 36px);
  max-height: 218px;
  margin: 0px 10px;
  margin-bottom: 28px;
  @media (max-width: 730px) {
    width: 100%;
    margin: 0;
    margin-bottom: 28px;
  }
`;
const CustomCarousel = styled(Carousel)`
  max-height: 140px;
  height: 100%;
`;
const DeleteBtn = styled.button`
  position: absolute;
  z-index: 2;
  right: 24px;
  top: 16px;
  background-color: transparent;
  outline: none;
  border: none;
  padding: 0;
  & :hover {
    opacity: 0.6;
  }
`;
const Icon = styled.img`
  max-height: 28px;
  max-width: 28px;
`;

const RoomDetailContainer = styled.div`
  margin: 12px 0px 8px 0px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;
const RoomDetail = styled.p`
  font-style: normal;
  font-weight: normal;
  font-size: ${pxToRem(12)};
  line-height: ${pxToRem(14)};
`;

const NumberOfRooms = styled.p`
  font-weight: bold;
  font-size: ${pxToRem(8)};
  line-height: 120%;
  align-self: flex-end;
  text-align: right;
  letter-spacing: 0.5px;
  text-transform: capitalize;
  color: #a1a1a1;
  margin-bottom: 16px;
`;

const SliderRound = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 12px;
  ::before {
    border-radius: 50%;
    position: absolute;
    content: '';
    height: 12px;
    width: 12px;
    left: 4px;
    bottom: 2px;
    top: 2px;
    background-color: white;
    -webkit-transition: 0.4s;
    transition: 0.4s;
  }
`;
const Input = styled.input`
  :focus + ${SliderRound} {
    box-shadow: 0 0 1px #2f7ff6;
  }
  :checked + ${SliderRound} {
    background-color: #2f7ff6;
    ::before {
      -webkit-transform: translateX(12px);
      -ms-transform: translateX(12px);
      transform: translateX(12px);
    }
  }
`;

const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 32px;
  height: 16px;
  margin-right: 6px;
  ${Input} {
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

const AvaliablityContainer = styled.div`
  display: flex;
  align-self: flex-end;
  justify-content: flex-end;
  align-items: center;
  font-size: ${pxToRem(12)};
  line-height: ${pxToRem(14)};
`;
